import Review, {ReviewChecklistItem, ReviewItem, ReviewStatus} from "../../model/Review";
import Model from "../../model/Model";
import IModelItem from "../../model/IModelItem";
import {Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme} from "@mui/material";
import React, {ReactNode, useEffect, useState} from "react";
import DialogButton from "../../components/form/DialogButton";
import Income, {IncomeType} from "../../model/Income";
import Deduction from "../../model/Deduction";
import Tax from "../../model/Tax";
import Expense, {ExpenseCategory} from "../../model/Expense";
import Snapshot, {ISnapshotDetail, SnapshotDetail} from "../../model/Snapshot";
import {SnapshotEditDetail} from "../calc/networth/NetWorthSnapshotDialog";
import {
  createUUID,
  dateToLocalFormat, getISODateFromDate,
  getISODateStartOfYear,
  isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../stores/StoreUtilities";
import {
  UpdateDeductionInput,
  UpdateExpenseInput,
  UpdateIncomeInput, UpdateReviewInput,
  UpdateSnapshotInput,
  UpdateTaxInput
} from "../../API";
import {useResources} from "../../stores/ResourceProvider";
import Asset, {AssetCategory} from "../../model/Asset";
import IBudget from "../../model/iBudget";
import FormValidator from "../../components/form/FormValidator";
import BasicMenuButton from "../../components/controls/BasicMenuButton";
import {AddCircleRounded} from "@mui/icons-material";
import ModelStore from "../../stores/ModelStore";
import {
  DeductionExpenseIcon,
  FoodAndPersonalExpenseIcon,
  GivingAndMiscExpenseIcon,
  HousingExpenseIcon,
  IncomeIcon,
  InsuranceAndMedicalExpenseIcon,
  LoanExpenseIcon,
  TaxExpenseIcon,
  TravelAndEntertainmentExpenseIcon,
  VehiclesAndTransporationExpenseIcon,
} from "../../styles/Icons";
import {globalColors} from "../../styles/globalStyles";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Schedule, {ScheduleNames} from "../../model/Schedule";
import {
  foodAndPersonalCareExpenseOptions,
  givingAndMiscExpenseOptions,
  housingExpenseOptions,
  incomeMenuOptions,
  insuranceAndMedicalExpenseOptions,
  loanAndLiabilityExpenseOptions,
  taxMenuOptions,
  travelAndEntertainmentExpenseOptions,
  vehiclesAndTransportationExpenseOptions
} from "../calc/budget/BudgetView";
import IncomeEditDialog from "../calc/budget/IncomeEditDialog";
import DeductionEditDialog from "../calc/budget/DeductionEditDialog";
import TaxEditDialog from "../calc/budget/TaxEditDialog";
import ExpenseEditDialog from "../calc/budget/ExpenseEditDialog";
import SnapshotDetailEditDialog from "../calc/budget/SnapshotDetailEditDialog";
import Scheduled from "../../model/Scheduled";
import {endOfYear, startOfToday, startOfYear} from "date-fns";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import isEqual from "lodash.isequal";


const BudgetReview = ({
  review,
  model,
  updatedAt,
  isDialog,
  onChange,
  onChangeView,
  onClose,
  onDecline
} : {
  review: Review
  model: Model
  updatedAt: string
  isDialog?: boolean
  onChange?(item: IModelItem): void
  onChangeView?(view: string): void
  onClose?(): void
  onDecline?(review: Review): void
}) => {
  const [incomes, setIncomes] = useState<Income[]>([])
  const [deductions, setDeductions] = useState<Deduction[]>([])
  const [taxes, setTaxes] = useState<Tax[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [housingExpenses, setHousingExpenses] = useState<Expense[]>([])
  const [loanAndLiabilityExpenses, setLoanAndLiabilityExpenses] = useState<Expense[]>([])
  const [foodAndPersonalCareExpenses, setFoodAndPersonalCareExpenses] = useState<Expense[]>([])
  const [insuranceAndMedicalExpenses, setInsuranceAndMedicalExpenses] = useState<Expense[]>([])
  const [vehiclesAndTransportationExpenses, setVehiclesAndTransportationExpenses] = useState<Expense[]>([])
  const [travelAndEntertainmentExpenses, setTravelAndEntertainmentExpenses] = useState<Expense[]>([])
  const [givingAndMiscExpenses, setGivingAndMiscExpenses] = useState<Expense[]>([])
  const [baseSnap, setBaseSnap] = useState<Snapshot | undefined>()
  const [snapshot, setSnapshot] = useState<Snapshot | undefined>()
  const [snapshotEditItems, setSnapshotEditItems] = useState<SnapshotEditDetail[]>([])
  const [reviewDate, setReviewDate] = useState<Date | undefined>()
  const [editIncome, setEditIncome] = useState<Income | undefined>()
  const [editDeduction, setEditDeduction] = useState<Deduction | undefined>()
  const [editTax, setEditTax] = useState<Tax | undefined>()
  const [editExpense, setEditExpense] = useState<Expense | undefined>()
  const [editDetail, setEditDetail] = useState<SnapshotEditDetail | undefined>()

  useEffect(() => {
    if (model) {
      setIncomes(model.incomes)
      setDeductions(model.deductions)
      setTaxes(model.taxes)
      setExpenses(model.expenses)
      setHousingExpenses(model.getExpensesByCategory(ExpenseCategory.Housing))
      setLoanAndLiabilityExpenses(model.getExpensesByCategory(ExpenseCategory.LoansAndLiabilities))
      setFoodAndPersonalCareExpenses(model.getExpensesByCategory(ExpenseCategory.FoodAndPersonalCare))
      setInsuranceAndMedicalExpenses(model.getExpensesByCategory(ExpenseCategory.InsuranceAndMedical))
      setVehiclesAndTransportationExpenses(model.getExpensesByCategory(ExpenseCategory.VehiclesAndTransportation))
      setTravelAndEntertainmentExpenses(model.getExpensesByCategory(ExpenseCategory.TravelAndEntertainment))
      setGivingAndMiscExpenses(model.getExpensesByCategory(ExpenseCategory.GivingAndMisc))
    }

    if (review.snapshotId) {
      setReviewDate(isoToLocalDate(review.reviewDate))
      const snap = model.snapshots.find((s: Snapshot) => s.id === review.snapshotId)
      if (snap) {
        setSnapshot(snap)
        setSnapshotEditItems(snap.details.map((item: ISnapshotDetail) => new SnapshotEditDetail(item)))
      }
      const base = model.snapshots.find((s: Snapshot) => s.id === review.baseSnapId)
      if (base) {
        setBaseSnap(base)
      }
    }
  }, [model, updatedAt])

  const {modelStore, userStore, calculator} = useResources()

  const theme = useTheme()

  const styles = {
    tr: {
      maxHeight: "32px !important",
    },
    th: {
      fontSize: 14,
      whiteSpace: "nowrap"
    },
    tb: {
      maxHeight: "32px !important",
      py: 0,
      whiteSpace: "nowrap"
    },
  }

  const handleUpdate = async (item: IBudget) => {
    const typename = item.getTypename()
    const index = snapshotEditItems.findIndex((detail: SnapshotEditDetail) => detail.id === item.id && detail.typename === typename)
    if (index >= 0) {
      const editItem = snapshotEditItems[index]
      editItem.amount = numberToMoneyFormat(item.amount)
      editItem.schedule = item.schedule
      setSnapshotEditItems([...snapshotEditItems])
    } else {
      if (item instanceof Income) {
        const income = item as Income
        setIncomes([...incomes, income])
      } else if (item instanceof Deduction) {
        const deduction = item as Deduction
        setDeductions([...deductions, deduction])
      } else if (item instanceof Tax) {
        const tax = item as Tax
        setTaxes([...taxes, tax])
      } else if (item instanceof Expense) {
        const expense = item as Expense
        // TODO add to array, if needed
        switch (expense.expenseCategory) {
          case ExpenseCategory.Housing:
            setHousingExpenses([...housingExpenses, expense])
            break
          case ExpenseCategory.FoodAndPersonalCare:
            setFoodAndPersonalCareExpenses([...foodAndPersonalCareExpenses, expense])
            break
          case ExpenseCategory.VehiclesAndTransportation:
            setVehiclesAndTransportationExpenses([...vehiclesAndTransportationExpenses, expense])
            break
          case ExpenseCategory.InsuranceAndMedical:
            setInsuranceAndMedicalExpenses([...insuranceAndMedicalExpenses, expense])
            break
          case ExpenseCategory.LoansAndLiabilities:
            setLoanAndLiabilityExpenses([...loanAndLiabilityExpenses, expense])
            break
          case ExpenseCategory.TravelAndEntertainment:
            setTravelAndEntertainmentExpenses([...travelAndEntertainmentExpenses, expense])
            break
          case ExpenseCategory.GivingAndMisc:
            setGivingAndMiscExpenses([...givingAndMiscExpenses, expense])
            break
        }
        setExpenses([...expenses, expense])
      }

      snapshotEditItems.push(new SnapshotEditDetail({id: item.id, typename: typename, balance: 0, amount: item.amount, schedule: item.schedule}))
      setSnapshotEditItems([...snapshotEditItems])
    }

    await handleSave()
  }

  const handleSave = async () => {
    if (!snapshot) {
      return
    }
    const snapshotDate = snapshot.date

    const details = snapshotEditItems.map((item: SnapshotEditDetail) => {
      return new SnapshotDetail({id: item.id, typename: item.typename, balance: moneyToNumberFormat(item.balance, 0), amount: item.amount !== undefined ? moneyToNumberFormat(item.amount) : undefined, schedule: item.schedule })
    })

    // Update snapshot
    const input: UpdateSnapshotInput = {
      id: snapshot.id,
      details: JSON.stringify(details)
    }

    const update = await modelStore.updateSnapshot(input)

    // Only update the budget items if this is the latest snapshot
    if (update) {
      const index = model.snapshots.findIndex((s: Snapshot) => s.id === update.id)
      if (index === 0) {
        // TODO: Move this to when the review is submitted
        // Update Budget amounts
        for (let detail of update.details) {
          switch (detail.typename) {
            case "Income":
              const income = incomes.find((i: Income) => i.id === detail.id)
              if (income && (income.amount !== detail.amount || !isEqual(income.schedule, detail.schedule))) {
                const input: UpdateIncomeInput = {
                  id: income.id,
                  amount: detail.amount,
                  amountDate: getISODateFromDate(snapshotDate),
                  schedule: JSON.stringify(detail.schedule)
                }
                await modelStore.updateIncome(input)
              }
              break
            case "Deduction":
              const deduction = deductions.find((i: Deduction) => i.id === detail.id)
              if (deduction && (deduction.amount !== detail.amount || !isEqual(deduction.schedule, detail.schedule))) {
                const input: UpdateDeductionInput = {
                  id: deduction.id,
                  amount: detail.amount,
                  amountDate: getISODateFromDate(snapshotDate),
                  schedule: JSON.stringify(detail.schedule)
                }
                await modelStore.updateDeduction(input)
              }
              break
            case "Tax":
              const tax = taxes.find((i: Tax) => i.id === detail.id)
              if (tax && (tax.amount !== detail.amount || !isEqual(tax.schedule, detail.schedule))) {
                const input: UpdateTaxInput = {
                  id: tax.id,
                  amount: detail.amount,
                  amountDate: getISODateFromDate(snapshotDate),
                  schedule: JSON.stringify(detail.schedule)
                }
                await modelStore.updateTax(input)
              }
              break
            case "Expense":
              const expense = expenses.find((i: Expense) => i.id === detail.id)
              if (expense && (expense.amount !== detail.amount || !isEqual(expense.schedule, detail.schedule))) {
                const input: UpdateExpenseInput = {
                  id: expense.id,
                  amount: detail.amount,
                  amountDate: getISODateFromDate(snapshotDate),
                  schedule: JSON.stringify(detail.schedule)
                }
                await modelStore.updateExpense(input)
              }
              break
          }
        }
      }
    }

    await updateChecklist(ReviewItem.Budget, true)

    if (update && onChange) {
      onChange(update)
    }
  }

  const updateChecklist = async (item: ReviewItem, checked: boolean) => {
    if (review.checklist) {
      const index = review.checklist.findIndex((checklistItem: ReviewChecklistItem) => checklistItem.reviewItem === item)
      if (index >= 0) {
        review.checklist[index].completed = checked
        const input: UpdateReviewInput = {
          id: review.id,
          checklist: JSON.stringify(review.checklist),
          status: ReviewStatus.InProgress
        }

        await userStore.updateReview(input)
      }
    }
  }

  // Income methods
  
  const getIncomeMenuOptions = () => {
    return incomeMenuOptions.map((item: Income) => item.description)
  }

  const handleAddIncome = (index: number) => {
    const option = incomeMenuOptions[index]
    if (model && option && snapshot) {
      const income = new Income({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        incomeType: option.incomeType,
        description: option.description,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: option.incomeType !== IncomeType.SocialSecurityBenefits ? getISODateStartOfYear() : undefined,
        amount: 0,
        amountDate: getISODateFromDate(snapshot ? snapshot.date : startOfToday()),
        sortOrder: incomes.length > 0 ? incomes[incomes.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setEditIncome(income)
    }
  }

  const handleEditDetail = (editIndex: number) => {
    setEditDetail(snapshotEditItems[editIndex])
  }
  
  // Deduction methods

  const getDeductionMenuOptions = () => {
    let options: string[] = []
    if (model) {
      const assets = model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets)
        .filter((a: Asset) => {
          // Filter out assets that already have a deduction
          return (model.deductions.find((d: Deduction) => d.assetId === a.id) === undefined)
        })
      options = assets.map((a: Asset) => a.description)
    }
    return [...options, 'Other']
  }

  const handleAddDeduction = (index: number) => {
    const options = getDeductionMenuOptions()
    const option = options[index]

    if (model && option) {
      const deduction = new Deduction({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        description: option,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateFromDate(snapshot ? snapshot.date : startOfToday()),
        sortOrder: deductions.length > 0 ? deductions[deductions.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      if (option !== 'Other' && model) {
        const asset = model.getAssetByDescription(option)
        if (asset) {
          deduction.assetId = asset.id
          deduction.ownerId = asset.ownerId
          deduction.owner = asset.owner
        }
      }
      setEditDeduction(deduction)
    }
  }
  
  // Tax methods

  const getTaxMenuOptions = () => {
    return taxMenuOptions.map((item: Tax) => item.description)
  }

  const handleAddTax = (index: number) => {
    const option = taxMenuOptions[index]
    if (model && option) {
      const tax = new Tax({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        description: option.description,
        taxType: option.taxType,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateFromDate(snapshot ? snapshot.date : startOfToday()),
        sortOrder: taxes.length > 0 ? taxes[taxes.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setEditTax(tax)
    }
  }

  // Expense methods

  const handleAddExpense = (index: number, expenseMenuOptions: Expense[], expenseGroup: Expense[]) => {
    const option = expenseMenuOptions[index]
    if (model && option) {
      const expense = new Expense({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        description: option.description,
        expenseCategory: option.expenseCategory,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateFromDate(snapshot ? snapshot.date : startOfToday()),
        sortOrder: expenseGroup.length > 0 ? expenseGroup[expenseGroup.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setEditExpense(expense)
    }
  }


  const handlePrev = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("networth")
    }
  }

  const handleNext = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("complete")
    }
  }

  const handleCancel = async (event: any) => {
    await handleSave()
    const input: UpdateReviewInput = {
      id: review.id,
      status: ReviewStatus.InProgress
    }

    const update = await userStore.updateReview(input)
    if (update && onChange) {
      onChange(update)
    }
    if (update && onClose) {
      onClose()
    }
  }

  const handleDecline = async (event: any) => {
    if (onDecline) {
      onDecline(review)
    }
  }

  const handleChangeAmount = (item: IBudget, value: string, editIndex: number) => {
    if (editIndex < 0) {
      const typename = item.getTypename()
      const editItem = new SnapshotEditDetail({id: item.id, typename: typename, balance: 0, amount: item.amount, schedule: item.schedule})
      snapshotEditItems.push(editItem)
      editIndex = snapshotEditItems.length - 1
    }
    snapshotEditItems[editIndex].amount = value
    setSnapshotEditItems([...snapshotEditItems])
  }

  const getAnnualTotal = (item: IBudget, amount: number, schedule: Schedule, snapshotDate: Date) => {
    const scheduled = item as unknown as Scheduled
    const snapshotDetail: ISnapshotDetail = {
      id: item.id,
      typename: item.getTypename(),
      balance: 0,
      amount: amount,
      schedule: schedule
    }
    const occurrences = calculator.getScheduledOccurrences(scheduled, startOfYear(snapshotDate), endOfYear(snapshotDate), snapshotDetail)
    let total = 0
    for (const {date} of occurrences) {
      const futureAmount = calculator.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
      total += futureAmount
    }

    return total
  }

  const renderItem = (item: IBudget) => {
    let icon
    if (item instanceof Income) {
      icon = <IncomeIcon/>
    } else if (item instanceof Deduction) {
      icon = <DeductionExpenseIcon/>
    } else if (item instanceof Tax) {
      icon = <TaxExpenseIcon/>
    } else if (item instanceof Expense) {
      switch (item.expenseCategory) {
        case ExpenseCategory.Housing:
          icon = <HousingExpenseIcon/>
          break
        case ExpenseCategory.LoansAndLiabilities:
          icon = <LoanExpenseIcon/>
          break
        case ExpenseCategory.FoodAndPersonalCare:
          icon = <FoodAndPersonalExpenseIcon/>
          break
        case ExpenseCategory.InsuranceAndMedical:
          icon = <InsuranceAndMedicalExpenseIcon/>
          break
        case ExpenseCategory.VehiclesAndTransportation:
          icon = <VehiclesAndTransporationExpenseIcon/>
          break
        case ExpenseCategory.TravelAndEntertainment:
          icon = <TravelAndEntertainmentExpenseIcon/>
          break
        case ExpenseCategory.GivingAndMisc:
          icon = <GivingAndMiscExpenseIcon/>
          break
      }
    } else {
      icon = <GivingAndMiscExpenseIcon/>
    }

    const typename = item.getTypename()

    let baseAmount = 0
    let baseSchedule = Schedule.getDefaultSchedule("Monthly")
    if (baseSnap) {
      const baseDetail = baseSnap.getSnapshotDetail(item)
      if (baseDetail) {
        baseAmount = baseDetail.amount ?? 0
        if (baseDetail.schedule) {
          baseSchedule = baseDetail.schedule
        }
      }
    }

    let editIndex = snapshotEditItems.findIndex((detail: SnapshotEditDetail) => detail.id === item.id && detail.typename === typename)
    let amount = ""
    let schedule = baseSchedule
    const startDate = model.getDate(item.start)
    if (editIndex >= 0) {
      amount = snapshotEditItems[editIndex].amount ?? numberToMoneyFormat(baseAmount, 0)
      if (snapshotEditItems[editIndex].schedule) {
        schedule = snapshotEditItems[editIndex].schedule
      }
    } else if (startDate && (reviewDate && reviewDate.getTime() < startDate.getTime())) {
      // Don't display item if the snapshot date is before the item startDate
      return null
    }

    let changeColor = globalColors.iconContrast
    let changeIcon = <ArrowDropUpIcon/>
    let changePct = ""
    if (baseAmount > 0) {
      // changePct = numberToPercentFormat((moneyToNumberFormat(amount) - baseAmount) / baseAmount, 2)
      const baseTotal = getAnnualTotal(item, baseAmount, baseSchedule!, baseSnap!.date)
      const annualTotal = getAnnualTotal(item, moneyToNumberFormat(amount), schedule!, snapshot!.date)
      changePct = numberToPercentFormat((annualTotal - baseTotal) / baseTotal, 2)
    } else if (!amount) {
      changeIcon = <span></span>
    }
    if (changePct === "0%") {
      changePct = ""
      changeIcon = <span></span>
    } else if (changePct.startsWith("-")) {
      changeColor = globalColors.negative
      changeIcon = <ArrowDropDownIcon/>
      changePct = changePct.substring(1)
    } else {
      changeColor = globalColors.liquidAsset
    }

    return (
      <TableRow key={item.id} sx={styles.tr}>
        <TableCell sx={styles.tb}>{icon}</TableCell>
        <TableCell sx={styles.tb}>{item.description}</TableCell>
        <TableCell sx={styles.tb}>
          {baseSnap &&
            <Box display="flex" flexGrow={0} width={130} mr={2}>
              <TextFieldValidator
                margin="none"
                name={`base-${item.id}`}
                label=""
                type="text"
                fullWidth
                variant="standard"
                value={numberToMoneyFormat(baseAmount, 0)}
                helperText={baseSchedule?.getDescription(item.startDate)}
                disabled={true}
                InputLabelProps={{shrink: false}}
              />
            </Box>
          }
        </TableCell>
        <TableCell sx={styles.tb}>
          <TextFieldValidator
            margin="none"
            name={item.id}
            label=""
            type="text"
            fullWidth
            variant="standard"
            required
            validators={{ required: false, isMoney: true }}
            value={amount}
            helperText={schedule?.getDescription(item.startDate)}
            InputLabelProps={{shrink: false}}
            onChange={(event: any) => {
              handleChangeAmount(item, event.currentTarget.value, editIndex)
            }}
          />
        </TableCell>
        <TableCell sx={styles.tb}>
          <IconButton id={`${item.id}-edit-btn`}size="small"
                      sx={{paddingTop:0, paddingBottom:0}}
                      onClick={(event: any) => {
                        handleEditDetail(editIndex)
                      }}
          >
            <EditIcon sx={{width:18, height:18}}/>
          </IconButton>
        </TableCell>
        <TableCell sx={styles.tb}>
          <Box display="flex" p={0} whiteSpace="nowrap" color={changeColor}>
            {changeIcon} <Typography variant="body2" color={changeColor}>{changePct}</Typography>
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  const renderCategory = (title: string, items: IBudget[], typename: string, itemName: string, expenseCategory?: ExpenseCategory) => {
    let menuButton: ReactNode | null = null
    switch (typename) {
      case "Income":
        menuButton = <BasicMenuButton
          icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
          label={`New ${itemName}`}
          options={getIncomeMenuOptions()}
          onClickOption={handleAddIncome}
        />
        break
      case "Deduction":
        menuButton = <BasicMenuButton
          icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
          label={`New ${itemName}`}
          options={getDeductionMenuOptions()}
          onClickOption={handleAddDeduction}
        />
        break
      case "Tax":
        menuButton = <BasicMenuButton
          icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
          label={`New ${itemName}`}
          options={getTaxMenuOptions()}
          onClickOption={handleAddTax}
        />
        break
      case "Expense":
        let expenseMenuOptions: Expense[] = []
        let expenseGroup: Expense[]
        switch (expenseCategory) {
          case ExpenseCategory.Housing:
            expenseMenuOptions = housingExpenseOptions
            expenseGroup = housingExpenses
            break
          case ExpenseCategory.LoansAndLiabilities:
            expenseMenuOptions = loanAndLiabilityExpenseOptions
            expenseGroup = loanAndLiabilityExpenses
            break
          case ExpenseCategory.FoodAndPersonalCare:
            expenseMenuOptions = foodAndPersonalCareExpenseOptions
            expenseGroup = foodAndPersonalCareExpenses
            break
          case ExpenseCategory.InsuranceAndMedical:
            expenseMenuOptions = insuranceAndMedicalExpenseOptions
            expenseGroup = insuranceAndMedicalExpenses
            break
          case ExpenseCategory.VehiclesAndTransportation:
            expenseMenuOptions = vehiclesAndTransportationExpenseOptions
            expenseGroup = vehiclesAndTransportationExpenses
            break
          case ExpenseCategory.TravelAndEntertainment:
            expenseMenuOptions = travelAndEntertainmentExpenseOptions
            expenseGroup = travelAndEntertainmentExpenses
            break
          case ExpenseCategory.GivingAndMisc:
            expenseMenuOptions = givingAndMiscExpenseOptions
            expenseGroup = givingAndMiscExpenses
            break
        }
        if (expenseMenuOptions.length > 0) {
          let options = expenseMenuOptions.map((item: Expense) => item.description)
          menuButton = <BasicMenuButton
            icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
            label={`New ${itemName}`}
            options={options}
            onClickOption={(index: number) => handleAddExpense(index, expenseMenuOptions, expenseGroup)}
          />
        }
        break
    }

    const priorLabel =  baseSnap && (!snapshot || baseSnap.id !== snapshot.id) ?`${dateToLocalFormat(baseSnap.date, 'M/d/yy')} Balance` : 'Prior Balance'
    const newLabel = snapshot ? `${dateToLocalFormat(snapshot.date, 'M/d/yy')} Amount` : 'New Amount'

    return (
      <Box display="flex" width="100%" flexDirection="column" alignItems="flex-start" sx={{mb:2}}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={56} sx={styles.th}></TableCell>
              <TableCell sx={styles.th}><Typography variant="h5">{title}</Typography></TableCell>
              <TableCell width={178} sx={styles.th}>{priorLabel}</TableCell>
              <TableCell width={160} sx={styles.th}>{newLabel}</TableCell>
              <TableCell width={60} sx={styles.th}></TableCell>
              <TableCell width={130} sx={styles.th}>Change %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item: IBudget) =>
              renderItem(item)
            )}
          </TableBody>
        </Table>
        <Box ml={2.5}>
          {menuButton}
        </Box>
      </Box>
    )
  }

  const canFinishLater = onClose && !review.submittedOn
  const canDecline = onDecline && !review.managed
  const height = isDialog ? "calc(100vh - 220px)" : "calc(100vh - 140px)"

  return (
    <React.Fragment>
      <DialogContent sx={{ my:0, p:0, height:height, maxHeight:height, overflow:"scroll"}}>
        <Box sx={{display:"flex", flexDirection:"column", alignItems:"flex-start", mt:1, pl:0, pb:0, mb:1}}>
          <Typography variant="h2" color="primary" gutterBottom>Budget Review</Typography>
          <Typography variant="body2" gutterBottom>
            Please update your budget items (income, deductions & contributions, tax withholdings and expenses).
          </Typography>
        </Box>

        <Box sx={{width:"100%"}}>
          <FormValidator name="snapshotForm" id="snapshotForm">
            {renderCategory("Incomes", incomes, "Income", "Income")}
            {renderCategory("Deductions", deductions, "Deduction", "Deduction")}
            {renderCategory("Taxes", taxes, "Tax", "Tax")}
            {renderCategory("Housing", housingExpenses, "Expense", "Housing Expense", ExpenseCategory.Housing)}
            {renderCategory("Loans & Liabilities", loanAndLiabilityExpenses, "Expense", "Loan & Liability Expense", ExpenseCategory.LoansAndLiabilities)}
            {renderCategory("Food & Personal Care", foodAndPersonalCareExpenses, "Expense", "Food & Personal Expense", ExpenseCategory.FoodAndPersonalCare)}
            {renderCategory("Insurance & Medical", insuranceAndMedicalExpenses, "Expense", "Insurance & Medical Expense", ExpenseCategory.InsuranceAndMedical)}
            {renderCategory("Vehicles & Transportation", vehiclesAndTransportationExpenses, "Expense", "Vehicle & Transportation Expense", ExpenseCategory.VehiclesAndTransportation)}
            {renderCategory("Travel & Entertainment", travelAndEntertainmentExpenses, "Expense", "Travel & Entertainment Expense", ExpenseCategory.TravelAndEntertainment)}
            {renderCategory("Giving & Misc", givingAndMiscExpenses, "Expense", "Giving & Misc Expense", ExpenseCategory.GivingAndMisc)}
          </FormValidator>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="center">
          <Box display="flex" justifyContent="flex-start">
            {canFinishLater &&
              <DialogButton variant="secondary" onClick={handleCancel}>
                Sign Out & Finish Later
              </DialogButton>
            }
            {canDecline &&
              <DialogButton variant="secondary" spacing="left" onClick={handleDecline}>
                Decline
              </DialogButton>
            }
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <DialogButton variant="secondary" onClick={handlePrev}>
            Prev
          </DialogButton>
          <DialogButton variant="primary" spacing="left" onClick={handleNext}>
            Next
          </DialogButton>
        </Box>
      </DialogActions>

      {editIncome &&
        <IncomeEditDialog income={editIncome} model={model}
                          open={true}
                          onSave={(update: Income) => {
                            setEditIncome(undefined)
                            handleUpdate(update)
                          }}
                          onClose={() => {
                            setEditIncome(undefined)
                          }}/>}
      {editDeduction &&
        <DeductionEditDialog deduction={editDeduction} model={model}
                          open={true}
                          onSave={(update: Deduction) => {
                            setEditDeduction(undefined)
                            handleUpdate(update)
                          }}
                          onClose={() => {
                            setEditDeduction(undefined)
                          }}/>}
      {editTax &&
        <TaxEditDialog tax={editTax} model={model}
                          open={true}
                          onSave={(update: Tax) => {
                            setEditTax(undefined)
                            handleUpdate(update)
                          }}
                          onClose={() => {
                            setEditTax(undefined)
                          }}/>}
      {editExpense &&
        <ExpenseEditDialog expense={editExpense} model={model}
                          open={true}
                          onSave={(update: Expense) => {
                            setEditExpense(undefined)
                            handleUpdate(update)
                          }}
                          onClose={() => {
                            setEditExpense(undefined)
                          }}/>}
      {editDetail &&
        <SnapshotDetailEditDialog detail={editDetail}
                                  open={true}
                                  onSave={(detail: SnapshotEditDetail) => {
                                    const index = snapshotEditItems.findIndex((item: SnapshotEditDetail) => item.id === detail.id && item.typename === detail.typename)
                                    if (index >= 0) {
                                      snapshotEditItems[index] = detail
                                    }
                                    setEditDetail(undefined)
                                  }}
                                  onClose={() => setEditDetail(undefined)}
        />
      }
    </React.Fragment>
  )
}

export default BudgetReview